import { useNavigate } from "@tanstack/react-router"
import React, { useState } from "react"

import { trpc } from "../../lib/trpc"

interface Contact {
  id: string
  name?: string
  email?: string
  phone?: string
  company?: string
  position?: string
  quality: "hot" | "warm" | "cold"
  tags: string[]
  metadata: Record<string, any>
  conversations?: {
    id: string
    campaign?: { name: string }
  }[]
  createdAt: string
}

const QualityBadge: React.FC<{ quality: string }> = ({ quality }) => {
  const colors = {
    hot: "bg-red-100 text-destructive dark:bg-red-800 dark:text-red-300",
    warm: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-300",
    cold: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-300",
  }

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[quality as keyof typeof colors]}`}>
      {quality.charAt(0).toUpperCase() + quality.slice(1)}
    </span>
  )
}

const ContactsList: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [qualityFilter, setQualityFilter] = useState<string>("")
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)

  // Query contacts with filters
  const {
    data: contactsData,
    isLoading,
    refetch,
  } = trpc.crm.contacts.list.useQuery({
    search: searchTerm || undefined,
    quality: qualityFilter ? (qualityFilter as "hot" | "warm" | "cold") : undefined,
    limit: 50,
    offset: 0,
  })

  // Create contact mutation
  const createContactMutation = trpc.crm.contacts.create.useMutation({
    onSuccess: () => {
      setShowCreateModal(false)
      void refetch()
    },
  })

  // Bulk import mutation
  const bulkImportMutation = trpc.crm.contacts.bulkImport.useMutation({
    onSuccess: (result) => {
      setShowImportModal(false)
      void refetch()
      alert(`Imported ${result.totalImported} contacts. ${result.duplicates.length} duplicates skipped.`)
    },
  })

  const handleCreateContact = (formData: FormData) => {
    const name = formData.get("name") as string
    const email = formData.get("email") as string
    const phone = formData.get("phone") as string
    const company = formData.get("company") as string
    const position = formData.get("position") as string
    const quality = formData.get("quality") as string
    const primaryPlatform = formData.get("primaryPlatform") as string

    if (!email && !phone) {
      alert("Either email or phone is required")
      return
    }

    // Validate quality and platform
    const validQualities = ["hot", "warm", "cold"]
    const validPlatforms = ["sms", "email", "whatsapp", "messenger"]

    if (!validQualities.includes(quality) || !validPlatforms.includes(primaryPlatform)) {
      alert("Invalid quality or platform selected")
      return
    }

    createContactMutation.mutate({
      name: name || undefined,
      email: email || undefined,
      phone: phone || undefined,
      primaryPlatform: primaryPlatform as "sms" | "email" | "whatsapp" | "messenger",
      quality: quality as "hot" | "warm" | "cold",
      metadata: {
        company: company || undefined,
        position: position || undefined,
      },
    })
  }

  const handleImportContacts = (csvText: string) => {
    const lines = csvText.trim().split("\n")
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase())

    const contacts = lines
      .slice(1)
      .map((line) => {
        const values = line.split(",").map((v) => v.trim())
        const contact: any = {
          primaryPlatform: "email",
          quality: "warm",
          metadata: {},
        }

        headers.forEach((header, index) => {
          const value = values[index]
          if (!value) return

          switch (header) {
            case "name":
              contact.name = value
              break
            case "email":
              contact.email = value
              break
            case "phone":
              contact.phone = value
              break
            case "company":
              contact.metadata.company = value
              break
            case "position":
              contact.metadata.position = value
              break
            case "quality":
              if (["hot", "warm", "cold"].includes(value.toLowerCase())) {
                contact.quality = value.toLowerCase()
              }
              break
          }
        })

        return contact
      })
      .filter((contact) => contact.email || contact.phone)

    if (contacts.length === 0) {
      alert("No valid contacts found in CSV")
      return
    }

    bulkImportMutation.mutate({
      contacts,
      skipDuplicates: true,
    })
  }

  return (
    <div className="h-full overflow-auto bg-foregroundx ">
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold  dark:text-white">Contacts</h1>
              <p className="mt-2  dark:text-gray-400">Manage your contact database</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowImportModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <span>📥</span>
                Import CSV
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <span>+</span>
                Add Contact
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
            />
          </div>
          <select
            value={qualityFilter}
            onChange={(e) => setQualityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background   dark:text-white"
          >
            <option value="">All Qualities</option>
            <option value="hot">Hot</option>
            <option value="warm">Warm</option>
            <option value="cold">Cold</option>
          </select>
        </div>

        {/* Contacts List */}
        <div className="bg-background  rounded-lg shadow-sm border border-border dark:border-gray-700">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="text-gray-500">Loading contacts...</div>
            </div>
          ) : contactsData?.contacts && contactsData.contacts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-foregroundx dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Quality
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Conversations
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {contactsData.contacts.map((contact) => (
                    <tr key={contact.id} className=" dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <div>
                          <button
                            onClick={() => navigate({ to: `/crm/contacts/${contact.id}` })}
                            className="font-medium  dark:text-blue-400 hover:underline"
                          >
                            {contact.name || contact.email || contact.phone || "Unknown"}
                          </button>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {contact.email && <div>{contact.email}</div>}
                            {contact.phone && <div>{contact.phone}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm  dark:text-white">
                          {(contact.metadata as any)?.company || (contact as any).company || "-"}
                        </div>
                        {((contact.metadata as any)?.position || (contact as any).position) && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {(contact.metadata as any)?.position || (contact as any).position}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <QualityBadge quality={contact.quality} />
                      </td>
                      <td className="px-6 py-4 text-sm  dark:text-white">{contact.conversations?.length || 0}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(contact.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate({ to: `/crm/contacts/${contact.id}` })}
                            className=" hover:text-blue-800 text-sm"
                          >
                            View
                          </button>
                          <button
                            onClick={() =>
                              navigate({
                                to: "/crm/start-conversation",
                                // search: () => ({ contactId: contact.id })
                              })
                            }
                            className="text-green-600 hover:text-green-800 text-sm"
                          >
                            Start Chat
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="text-gray-500 mb-4">No contacts found</div>
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Contact
                </button>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Import CSV
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Create Contact Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">Add New Contact</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleCreateContact(new FormData(e.currentTarget))
                }}
              >
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Name</label>
                    <input
                      name="name"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Enter contact name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Email</label>
                    <input
                      name="email"
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Phone</label>
                    <input
                      name="phone"
                      type="tel"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Company</label>
                    <input
                      name="company"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Enter company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Position</label>
                    <input
                      name="position"
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      placeholder="Enter job title"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">Quality</label>
                      <select
                        name="quality"
                        defaultValue="warm"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      >
                        <option value="hot">Hot</option>
                        <option value="warm">Warm</option>
                        <option value="cold">Cold</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                        Primary Platform
                      </label>
                      <select
                        name="primaryPlatform"
                        defaultValue="email"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white"
                      >
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="whatsapp">WhatsApp</option>
                        <option value="messenger">Messenger</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createContactMutation.isLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createContactMutation.isLoading ? "Adding..." : "Add Contact"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-background  rounded-lg p-6 w-full max-w-lg">
              <h2 className="text-xl font-semibold  dark:text-white mb-4">Import Contacts from CSV</h2>
              <div className="space-y-4">
                <div className="text-sm  dark:text-gray-400">
                  <p className="mb-2">Expected CSV format:</p>
                  <code className="bg-foregroundx dark:bg-gray-700 p-2 rounded text-xs block">
                    name,email,phone,company,position,quality
                    <br />
                    John Doe,john@example.com,+1234567890,Acme Corp,Developer,hot
                  </code>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary dark:text-gray-300 mb-1">
                    Paste CSV Content
                  </label>
                  <textarea
                    id="csvInput"
                    rows={10}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-background dark:bg-gray-700  dark:text-white font-mono text-sm"
                    placeholder="Paste your CSV content here..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2  dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const csvText = (document.getElementById("csvInput") as HTMLTextAreaElement).value
                    if (csvText.trim()) {
                      handleImportContacts(csvText)
                    }
                  }}
                  disabled={bulkImportMutation.isLoading}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {bulkImportMutation.isLoading ? "Importing..." : "Import Contacts"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ContactsList
